import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useNextStep } from 'nextstepjs';
import { getOnboardingState, updateOnboardingStep, clearOnboardingState } from '@/lib/onboarding';
import { getActiveTour, shouldShowTour } from '@/lib/tours';
import { useEffectiveBranding } from '@/hooks/use-effective-branding';
import type { Tour } from 'nextstepjs';

/**
 * Hook to manage onboarding tours based on current state and URL
 */
export function useOnboardingTour() {
  const location = useLocation();
  const { startNextStep, closeNextStep } = useNextStep();
  const brand = useEffectiveBranding();
  const [currentTours, setCurrentTours] = useState<Tour[]>([]);
  const [activeTourName, setActiveTourName] = useState<string | null>(null);

  useEffect(() => {
    const onboardingState = getOnboardingState();
    const currentPath = location.pathname;

    // Check if we should show an onboarding tour
    if (!shouldShowTour(onboardingState, currentPath)) {
      // Only clear onboarding-specific state, don't close other tours
      // Other tours (like funnel-editor-setup) are managed by their own hooks
      setCurrentTours([]);
      setActiveTourName(null);
      return;
    }

    // Get the active tour configuration for onboarding
    const tourConfig = getActiveTour(onboardingState, currentPath, brand.appName);

    if (tourConfig) {
      setCurrentTours(tourConfig.tours);
      setActiveTourName(tourConfig.startTour);

      // Small delay to let the page render before starting tour
      const timer = setTimeout(() => {
        startNextStep(tourConfig.startTour);
      }, 500);

      return () => clearTimeout(timer);
    } else {
      // No onboarding tour config for this path - clear onboarding state only
      setCurrentTours([]);
      setActiveTourName(null);
    }
  }, [location.pathname, startNextStep, brand.appName]);

  // Handler for tour completion
  const handleTourComplete = (tourName: string | null) => {
    console.log('Tour completed:', tourName);

    const onboardingState = getOnboardingState();
    if (!onboardingState) return;

    // Update step based on completed tour
    if (tourName === 'account-creation') {
      updateOnboardingStep('org_created');
    } else if (tourName === 'store-creation') {
      updateOnboardingStep('store_created');

      // If this was a generic onboarding (no plugin), clear state after store creation
      if (onboardingState.intent?.type === 'store_create') {
        console.log('✅ Generic onboarding complete - clearing state');
        clearOnboardingState();
      }
    } else if (tourName === 'plugin-installation') {
      // Plugin installation complete - clear onboarding
      clearOnboardingState();
    }

    closeNextStep();
  };

  // Handler for tour skip
  const handleTourSkip = (step: number, tourName: string | null) => {
    console.log('Tour skipped at step:', step, 'tour:', tourName);

    // Don't clear state on skip - user can resume later
    closeNextStep();
  };

  // Handler for step change
  const handleStepChange = (step: number, tourName: string | null) => {
    // Only validate tours that are part of the onboarding system
    // Other tours (like funnel-editor-setup) are managed separately
    const currentTour = currentTours.find((t) => t.tour === tourName);

    // If tour is not in currentTours, it's managed elsewhere - don't interfere
    if (!currentTour) {
      return;
    }

    // Guard: If tour has no steps, complete it
    if (!currentTour.steps || currentTour.steps.length === 0) {
      handleTourComplete(tourName);
      return;
    }

    // Guard: If step is out of bounds, complete the tour instead of crashing
    // This prevents "Cannot read properties of undefined (reading 'title')" error
    if (step >= currentTour.steps.length) {
      handleTourComplete(tourName);
      return;
    }

    // Optional: Track analytics
    if ((window as any).analytics) {
      (window as any).analytics.track('Onboarding Tour Step', {
        step,
        tourName,
        path: location.pathname,
      });
    }
  };

  return {
    tours: currentTours,
    activeTourName,
    onComplete: handleTourComplete,
    onSkip: handleTourSkip,
    onStepChange: handleStepChange,
  };
}
